//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
  HelpContext, type HelpContextValue, type HelpMomentHandler,
  type StartTimerOptions
} from '@/contexts/HelpContext';
import { autoToursOff } from '@/components/Help/autoTours';
import { usePersistentState } from '@/hooks/usePersistentState';

interface HelpProviderProps {
  children: React.ReactNode;
}

// The opt-out list is read during render, by the gate that decides whether
// leaving a tour asks first. Anything but a list of ids throws there rather
// than in a callback, which the app-level boundary answers by blanking the
// app -- so a value of the wrong shape is treated as no opt-outs at all.
const isTourIdList = (stored: unknown): boolean =>
  Array.isArray(stored) && stored.every((id) => typeof id === 'string');

/**
 * HelpProvider - Manages help and tour related state.
 *
 * Core functionality:
 * - Single application-wide inactivity timer.
 * - User interaction detection to cancel help.
 * - Help indicator (pulsing) visibility control.
 */
export const HelpProvider = ({ children }: HelpProviderProps) => {
  // Tour state.
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [tourStep, setTourStep] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [optedOutTours, setOptedOutTours] =
    usePersistentState<string[]>('opted-out-tours', [], false, isTourIdList);

  // Help icon state.
  const [isPulsing, setIsPulsing] = useState(false);
  const [helpInitialized, setHelpInitialized] = useState(false);

  // Timer reference.
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read by the timer's callback, which closes over nothing current.
  const activeTourRef = useRef<string | null>(null);
  activeTourRef.current = activeTour;
  // Whoever asked for the help moment ahead of the icon.
  const helpMomentHandlerRef = useRef<HelpMomentHandler | null>(null);
  // The two rungs of the exit focus chain that the tour cannot work out for
  // itself. Held here because the tour is not mounted when either is set:
  // the origin is read as the tour starts, and the fallback is registered
  // by the Help button, which is up whether a tour is running or not.
  const tourOriginRef = useRef<HTMLElement | null>(null);
  const tourFocusFallbackRef = useRef<HTMLElement | null>(null);
  const tourFocus = useMemo(() => ({
    originRef: tourOriginRef, fallbackRef: tourFocusFallbackRef
  }), []);

  // Track if a timer is active.
  const [timerActive, setTimerActive] = useState(false);

  // Track timer settings for logging.
  const currentTimerLabel = useRef('none');

  // User interaction listeners reference.
  const interactionListenerRef = useRef<((event: Event) => void) | null>(null);
  const interactionListenersActive = useRef(false);

  const removeInteractionListeners = useCallback(() => {
    if (interactionListenersActive.current &&
        interactionListenerRef.current) {
      window.removeEventListener('mousedown', interactionListenerRef.current);
      window.removeEventListener('keydown', interactionListenerRef.current);
      interactionListenersActive.current = false;
    }
  }, []);

  const setupInteractionListeners = useCallback(() => {
    // Ensure we never stack listeners across timer restarts.
    removeInteractionListeners();

    // Create handler function.
    const handler = (_event: Event) => {
      // Cancel any pending timer.
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
        setTimerActive(false);
        currentTimerLabel.current = 'none';
      }

      // Hide indicators.
      setIsPulsing(false);

      // Remove listeners.
      removeInteractionListeners();
    };

    // Store the handler and add listeners.
    interactionListenerRef.current = handler;
    window.addEventListener('mousedown', interactionListenerRef.current);
    window.addEventListener('keydown', interactionListenerRef.current);
    interactionListenersActive.current = true;
  }, [removeInteractionListeners]);

  // Now the actual functionality that uses these helpers.

  // Start inactivity timer.
  const startInactivityTimer = useCallback(({
    timerSeconds = 10,
    label = 'default'
  }: StartTimerOptions = {}) => {
    // Clear any existing timer first.
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    // Update state.
    setTimerActive(true);
    currentTimerLabel.current = label;

    // Set up interaction listeners immediately to catch any activity.
    setupInteractionListeners();

    // Set the inactivity timer.
    inactivityTimerRef.current = setTimeout(() => {
      // The help moment. Offered first to whoever registered for it -- the
      // Getting Started tour, on an empty server -- and shown as the pulsing
      // icon only when nothing took it and no tour is already doing the
      // helping: a pulsing Help icon under a tour's overlay is a second
      // voice. Offered here rather than by whoever takes it, so the switch
      // that stops surfaces opening themselves is honored by all of them.
      const taken = autoToursOff() ?
                    false : helpMomentHandlerRef.current?.() ?? false;
      if (!taken && activeTourRef.current === null) setIsPulsing(true);
      setTimerActive(false);
      currentTimerLabel.current = 'none';

      // Timer is done.
      inactivityTimerRef.current = null;

      // Note: We keep the interaction listeners active to detect when to hide
      //       indicators.
    }, timerSeconds * 1000);

    return true;
  }, [setupInteractionListeners]);

  // Cancel inactivity timer.
  const cancelInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      // Clear timer.
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;

      // Update state.
      setTimerActive(false);
      currentTimerLabel.current = 'none';

      // Remove interaction listeners.
      removeInteractionListeners();

      return true;
    }

    return false;
  }, [removeInteractionListeners]);

  const setHelpMomentHandler = useCallback(
    (handler: HelpMomentHandler | null) => {
      helpMomentHandlerRef.current = handler;
    }, []);

  // Hide help indicators.
  const hideHelpIndicators = useCallback(() => {
    setIsPulsing(false);

    // Also cancel any active timer.
    cancelInactivityTimer();
  }, [cancelInactivityTimer]);

  // Show help indicators immediately (without timer).
  const showHelpIndicators = useCallback(() => {
    setIsPulsing(true);

    // Set up interaction listeners immediately.
    setupInteractionListeners();
  }, [setupInteractionListeners]);

  // Clean up timers and listeners on unmount.
  useEffect(() => {
    return () => {
      // Clear timer if active.
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      // Remove any listeners.
      removeInteractionListeners();
    };
  }, [removeInteractionListeners]);

  // Tour actions.
  const startTour = useCallback((tourId: string) => {
    // Read before the first step renders, which is the last moment the
    // answer is the user's rather than the tour's: Joyride takes the
    // keyboard into its tooltip as soon as it mounts. The body means the
    // keyboard was nowhere worth returning it to.
    const active = document.activeElement;
    tourOriginRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null;

    // Set tour state directly.
    setActiveTour(tourId);
    setTourStep(0);
  }, []);

  const endTour = useCallback(() => {
    setActiveTour(null);
    setTourStep(0);
    // A hold belongs to the step that asked for it, not to the next tour.
    setIsNextDisabled(false);
  }, []);

  const finishTour = useCallback(() => {
    // Read through the ref, which render keeps current: a setState updater
    // is not the place to start another update, and StrictMode runs it twice.
    const tourId = activeTourRef.current;
    if (tourId !== null) {
      setOptedOutTours((prev) =>
        prev.includes(tourId) ? prev : [...prev, tourId]);
    }
    endTour();
  }, [setOptedOutTours, endTour]);

  const contextValue: HelpContextValue = useMemo(() => ({
    // Tour state.
    tourState: {
      isNextDisabled,
      activeTour,
      tourStep,
      optedOutTours
    },

    // Help indicator state.
    helpState: {
      isPulsing,
      helpInitialized,
      timerActive
    },

    // Tour operations.
    tourActions: {
      setIsNextDisabled,
      startTour,
      endTour,
      setTourStep,
      finishTour
    },

    // Help operations.
    helpActions: {
      showHelpIndicators,
      hideHelpIndicators,
      startInactivityTimer,
      cancelInactivityTimer,
      setHelpInitialized,
      setHelpMomentHandler
    },

    // Exit focus rungs.
    tourFocus
  }), [isNextDisabled, activeTour, tourStep, optedOutTours, isPulsing,
       helpInitialized, timerActive, startTour, endTour, finishTour,
       showHelpIndicators, hideHelpIndicators, startInactivityTimer,
       cancelInactivityTimer, setHelpMomentHandler, tourFocus]);

  return (
    <HelpContext.Provider value={contextValue}>
      {children}
    </HelpContext.Provider>
  );
};
